import {InfoButton, Observer, P, RunInAction, RunInAction_Set, SpeechRecognizer, TextPlus, VDateTime, VMediaRecorder} from "web-vcore";
import {Clone, GetEntries, StartDownload} from "js-vextensions";
import Moment from "moment";
import React from "react";
import {Button, CheckBox, Column, Row, Select, Spinner, Text, TextArea, TextInput} from "react-vcomponents";
import {BaseComponentPlus} from "react-vextensions";
import {ShowMessageBox} from "react-vmessagebox";
import {ScrollView} from "react-vscrollview";
import {DeleteJournalEntry} from "../../../Server/Commands/JournalEntryCommands.js";
import {UpdateJournalEntry} from "../../../Server/Commands/JournalEntryCommands.js";
import {JournalEntry, JournalSegment, SleepPosition} from "../../../Server/DB/journalEntries/@JournalEntry.js";
import {store} from "../../../Store/index.js";
import {AudioChunkPlayer} from "../../../Utils/ReactComponents/AudioChunkPlayer.js";
import {ES} from "web-vcore";
import {Story} from "../../../Server/DB/stories.js";
import {FindMessagesInChain, GetStoryMessages} from "../../../Server/DB/storyMessages.js";
import {DeleteStory} from "../../../Server/Commands/StoryCommands.js";
import {UpdateStory} from "../../../Server/Commands/StoryCommands.js";
import moment from "moment";
import {StoryMessageUI} from "./StoryMessageUI.js";
import {ShowSummarizeRangeDialog} from "./SummarizeRangeDialog.js";
import {StoryMessage} from "../../../Server/DB/storyMessages/@StoryMessage.js";
import {observer_mgl} from "mobx-graphlink";
import {DoesMessagePassFilter} from "../../../Utils/Filters/StoryMessageFilter.js";
import {AddStoryMessage} from "../../../Server/Commands/StoryMessageCommands.js";

export const StoryUI = observer_mgl((props: {entry: Story})=>{
	const {entry} = props;
	const uiState = store.main.content;

	// general message processing
	// ----------

	const messages = GetStoryMessages(entry.creator, entry.id);
	const messages_summaries = messages.filter(a=>a.summary_chainEnd != null);
	/** A "normal" message is just any message that is not a "summary". */
	let messages_normal = messages.filter(a=>a.summary_chainEnd == null);

	const activeSummaryChains = messages_summaries.filter(a=>a.summary_enabled).map(summaryMessage=>{
		const sourceChain_endMessage = messages.find(a=>a.id == summaryMessage.summary_chainEnd);
		if (sourceChain_endMessage == null) return [];
		const isFirstMessageOfSummaryChain = (msg: StoryMessage)=>msg.parent == summaryMessage.parent;
		return FindMessagesInChain(messages, sourceChain_endMessage, false, isFirstMessageOfSummaryChain);
	});
	const messages_summarizedAway = activeSummaryChains.SelectMany(a=>a).Distinct().filter(a=>a.summary_chainEnd == null);

	const mainChain = messages_normal.length ? FindMessagesInChain(messages, messages_normal.Last(), true) : [];
	const verboseChain = messages_normal.length ? FindMessagesInChain(messages, messages_normal.Last(), false) : [];
	const mainChain_summaries = mainChain.filter(a=>a.summary_chainEnd != null);
	const mainChain_summarizedAway = verboseChain.filter(a=>!mainChain.includes(a));
	const messages_normal_offChain = messages_normal.filter(a=>!mainChain.includes(a) && !verboseChain.includes(a));

	// processing (eg. filtering) for display purposes
	// ----------

	const messageFilter = uiState.messageFilter.trim();
	let messages_normal_toShow = messages_normal;
	let messages_summaries_toShow = messages_summaries;
	// when a custom message-filter is active, stop doing the summary/side-branch filtering, and instead apply just that custom filter
	if (messageFilter.length > 0) {
		messages_normal_toShow = messages_normal_toShow.filter(a=>DoesMessagePassFilter(a, messageFilter));
		messages_summaries_toShow = messages_summaries_toShow.filter(a=>DoesMessagePassFilter(a, messageFilter));
	}
	// else, do regular filtering logic based on "hide" booleans, "keep last" values, etc.
	else {
		const messages_normal_offChain_onesToKeep = messages_normal_offChain.TakeLast(uiState.hideOffChainNormalMessages_enabled ? uiState.hideOffChainNormalMessages_keepLastX : 999999);
		messages_normal_toShow = messages_normal
			.filter(a=>uiState.hideSummarizedMessages ? !messages_summarizedAway.includes(a) : true)
			.filter(a=>uiState.hideOffChainNormalMessages_enabled ? (!messages_normal_offChain.includes(a) || messages_normal_offChain_onesToKeep.includes(a)) : true);
		messages_summaries_toShow = (
			uiState.hideSummaryMessages_enabled && uiState.hideSummaryMessages_keepLastX >= 0 ? messages_summaries.TakeLast(uiState.hideSummaryMessages_keepLastX) :
			uiState.hideSummaryMessages_enabled && uiState.hideSummaryMessages_keepLastX < 0 ? messages_summaries.filter(a=>a.summary_enabled).TakeLast(-uiState.hideSummaryMessages_keepLastX) :
			messages_summaries
		);
	}

	const getMainChainSortKey = (msg: StoryMessage)=>{
		// walk up parent chain to find the first self-or-ancestor that appears in the main chain
		let current: StoryMessage|n = msg;
		while (current != null) {
			const idx = mainChain.indexOf(current);
			if (idx != -1) return `${idx.toString().padStart(8, "0")}_${current.createdAt}`;
			current = messages.find(a=>a.id == current!.parent) ?? null;
		}
		// fallback: not connected to main chain at all — sort to end, then by createdAt
		return `99999999_${msg.createdAt}`;
	};
	const messagesToShow_chronological = [
		...messages_summaries_toShow,
		...messages_normal_toShow,
	].OrderBy(getMainChainSortKey);

	/*const mainChainMessages_hiddenAncestorChainLengths = new Map<StoryMessage, number>();
	let currentHiddenAncestorChainLength = 0;
	for (const message of mainChain_noSummarizing) {
		// if this main-chain message is also shown, store the built-up ancestor chain-length (if present), then reset the chain-length
		if (messagesToShow_chronological.includes(message)) {
			if (currentHiddenAncestorChainLength > 0) {
				mainChainMessages_hiddenAncestorChainLengths.set(message, currentHiddenAncestorChainLength);
			}
			currentHiddenAncestorChainLength = 0;
		}
		// if this main-chain message is hidden, simply increment the hidden-ancestor-chain-length
		else {
			currentHiddenAncestorChainLength++;
		}
	}*/

	return (
		<ScrollView style={ES({flex: 1})} contentStyle={{display: "flex", flexDirection: "column", backgroundColor: "hsla(0,0%,0%,.7)", borderRadius: 10, padding: 10}}>
			<Row style={{flexWrap: "wrap", gap: 5}}>
				<Button text="Back" onClick={()=>RunInAction_Set(()=>store.main.content.selectedStory = null)}/>
				<Button text="Delete" enabled={messages.length == 0} onClick={()=>{
					ShowMessageBox({
						title: "Delete story?", cancelButton: true,
						message: "Permanently delete this story?",
						onOK: ()=>{
							new DeleteStory({id: entry.id}).RunOnServer();
						},
					});
				}}/>
				<Button text="Export" onClick={()=>{
					// open dialog where user can input the id of a story-message, in which case it will only export that message and its ancestors
					let targetMessageID = "";
					const controller = ShowMessageBox({
						title: "Export story", cancelButton: true,
						message: ()=>{
							const Change = (..._)=>controller.UpdateUI();
							return (
								<>
									<Row>
										<Text>Target chain, final message ID: </Text>
										<TextInput style={{flex: 1}} value={targetMessageID} onChange={val=>Change(targetMessageID = val)}/>
									</Row>
									<Row>(leave blank to export all messages)</Row>
								</>
							);
						},
						onOK: async()=>{
							const targetMessage = messages.find(a=>a.id == targetMessageID);
							if (targetMessageID.length > 0 && targetMessage == null) {
								return void ShowMessageBox({title: "Error", message: "Specified message not found."});
							}

							const messagesToExport = targetMessage ? FindMessagesInChain(messages, targetMessage) : messages;
							const output = JSON.stringify({
								story: entry,
								messages: messagesToExport.map(a=>({...a, _id: a.id})),
							}, null, "\t");
							StartDownload(output, `StoryExport_${moment().format("YYYY-MM-DD-HH-mm-ss")}_${entry.title}.json`);
						},
					});
				}}/>
				<Button text="Summarize" onClick={()=>{
					ShowSummarizeRangeDialog({
						story: entry, messages,
						suggestedPartNumber: mainChain_summaries.length + 1,
						messagesToSummarize_firstId: uiState.mark_1 ?? "",
						messagesToSummarize_lastId: uiState.mark_2 ?? "",
					});
				}}/>
				<Button text="Info" enabled={messages_normal.length > 0} onClick={()=>{
					// show dialog with some info, eg. the number of messages in chain where last-message overall is the chain end-point
					ShowMessageBox({
						title: "Story info",
						message: ()=>{
							return (
								<Column sel>
									<Text>Summary messages: {messages_summaries.length} ({mainChain_summarizedAway.length} summarized away on main chain)</Text>
									<Text>Main-chain messages: {mainChain.length}</Text>
									<Text>Verbose-chain messages (ie. without summarizations): {verboseChain.length}</Text>
									<Text>Off-chain, non-summary messages: {messages_normal_offChain.length}</Text>
								</Column>
							);
						},
					});
				}}/>
				<Text>Filter:</Text>
				<InfoButton ml={3} text={`
					Filters messages using space-separated tokens (all must match).
					Plain words must all be found within the message content (case-insensitive).
					Special filters: id:abc123, age<1h (units: s, m, h, d, w), role:user, role:assistant.
					When active, the other filters (hide summarized, hide summaries, hide side-branches) are not applied.
				`.AsMultiline(0)}/>
				<TextInput ml={3} style={{width: 150}} value={uiState.messageFilter} onChange={val=>RunInAction_Set(()=>uiState.messageFilter = val)}/>

				<Row style={{flexWrap: "wrap", gap: 5, padding: "0 5px", borderRadius: 5, background: "rgba(255,255,255,.2)", fontSize: 12}}>
					<Text style={{fontSize: 14}}>Hide:</Text>
					<CheckBox text={`Summarized (${messages_summarizedAway.length})`} value={uiState.hideSummarizedMessages} onChange={val=>RunInAction_Set(()=>uiState.hideSummarizedMessages = val)}/>
					<CheckBox text={`Summaries (${messages_summaries.length})`} value={uiState.hideSummaryMessages_enabled} onChange={val=>RunInAction_Set(()=>uiState.hideSummaryMessages_enabled = val)}/>
					{uiState.hideSummaryMessages_enabled &&
					<>
						<TextPlus info={`
							If positive X, hides all but the last X summary messages. ("last" determined by createdAt)
							If negative X, same behavior, except it filters out disabled summaries before grabbing the "last X".
						`.AsMultiline(0)}>Keep last:</TextPlus>
						<Spinner min={-Infinity} style={{width: 40}} value={uiState.hideSummaryMessages_keepLastX} onChange={val=>uiState.hideSummaryMessages_keepLastX = val}/>
					</>}
					<CheckBox text={`Off-chain (${messages_normal_offChain.length})`} value={uiState.hideOffChainNormalMessages_enabled} onChange={val=>RunInAction_Set(()=>uiState.hideOffChainNormalMessages_enabled = val)}/>
					<InfoButton text={`
						Note: This only affects the visibility for "normal"/non-summary off-chain messages. (same goes for displayed count)
						("off-chain" summaries can still get shown, depending on your summary-hiding settings)
					`.AsMultiline(0)}/>
					{uiState.hideOffChainNormalMessages_enabled &&
					<>
						<Text>Keep last:</Text>
						<Spinner min={0} style={{width: 40}} value={uiState.hideOffChainNormalMessages_keepLastX} onChange={val=>uiState.hideOffChainNormalMessages_keepLastX = val}/>
					</>}
				</Row>
			</Row>
			<Row mt={5}>
				<Text>Title: </Text>
				<TextInput style={{flex: 1}} value={entry.title} onChange={val=>{
					new UpdateStory({id: entry.id, updates: {title: val}}).RunOnServer();
				}}/>
			</Row>
			<Row center mt={5}>
				<Text mr={5}>Created at: {moment(entry.createdAt).format("YYYY-MM-DD HH:mm:ss")}</Text>
			</Row>
			<Row mt={5}>
				<Text>Prompt variables:</Text>
				<InfoButton ml={5} text="Variables that are substituted into {{...}} expressions in the system prompt and standard addendum before sending to the LLM."/>
				<Button ml={5} mdIcon="plus" onClick={()=>{
					const newVars = [...(entry.promptVariables ?? []), {name: "", value: ""}];
					new UpdateStory({id: entry.id, updates: {promptVariables: newVars}}).RunOnServer();
				}}/>
			</Row>
			{(entry.promptVariables ?? []).map((variable, index)=>{
				return (
					<Row key={index} mt={index == 0 ? 0 : 5}>
						<Text>Name:</Text>
						<TextInput ml={5} style={{flex: 50}} value={variable.name} onChange={val=>{
							const newVars = [...(entry.promptVariables ?? [])];
							newVars[index] = {...newVars[index], name: val};
							new UpdateStory({id: entry.id, updates: {promptVariables: newVars}}).RunOnServer();
						}}/>
						<Text ml={5}>Value:</Text>
						<TextInput ml={5} style={{flex: 50}} value={variable.value} onChange={val=>{
							const newVars = [...(entry.promptVariables ?? [])];
							newVars[index] = {...newVars[index], value: val};
							new UpdateStory({id: entry.id, updates: {promptVariables: newVars}}).RunOnServer();
						}}/>
						<Button ml={5} mdIcon="arrow-up" title="Move up" enabled={index > 0} onClick={()=>{
							const newVars = [...(entry.promptVariables ?? [])];
							const temp = newVars[index - 1];
							newVars[index - 1] = newVars[index];
							newVars[index] = temp;
							new UpdateStory({id: entry.id, updates: {promptVariables: newVars}}).RunOnServer();
						}}/>
						<Button ml={5} mdIcon="arrow-down" title="Move down" enabled={index < (entry.promptVariables ?? []).length - 1} onClick={()=>{
							const newVars = [...(entry.promptVariables ?? [])];
							const temp = newVars[index + 1];
							newVars[index + 1] = newVars[index];
							newVars[index] = temp;
							new UpdateStory({id: entry.id, updates: {promptVariables: newVars}}).RunOnServer();
						}}/>
						<Button ml={5} mdIcon="delete" onClick={()=>{
							ShowMessageBox({
								title: "Delete variable?", cancelButton: true,
								message: `Permanently delete this variable?`,
								onOK: ()=>{
									const newVars = (entry.promptVariables ?? []).filter((_, i)=>i != index);
									new UpdateStory({id: entry.id, updates: {promptVariables: newVars}}).RunOnServer();
								},
							});
						}}/>
					</Row>
				);
			})}
			<Row mt={5}>
				<Text>Story system prompt:</Text>
				<InfoButton ml={5} text="An optional system prompt specific to this story. It is appended after the engine config's system prompt (extracted from the start-prompt field) when generating messages."/>
			</Row>
			<TextArea mt={5} autoSize={true} value={entry.systemPrompt ?? ""} onChange={val=>{
				new UpdateStory({id: entry.id, updates: {systemPrompt: val}}).RunOnServer();
			}}/>
			<Row mt={5}>
				<Text>Standard addendum:</Text>
				<InfoButton ml={5} text={`
					An optional addendum attached to the latest user message sent to the LLM.
					Useful for reminder instructions. (eg. "Re-read the system prompt and ensure your response follows all its rules.")
					Text outside <pre></pre> tags is appended to the end; text inside <pre></pre> tags is prepended to the start. Both can be used together.
				`}/>
			</Row>
			<TextArea mt={5} autoSize={true} value={entry.standardAddendum ?? ""} onChange={val=>{
				new UpdateStory({id: entry.id, updates: {standardAddendum: val}}).RunOnServer();
			}}/>
			<Row mt={5} mb={5}>
				<Text>Messages:</Text>
				<Button ml={5} mdIcon="plus" onClick={()=>{
					const newMessage = new StoryMessage({story: entry.id, role: "user", model: null, content: ""});
					new AddStoryMessage({entry: newMessage}).RunOnServer();
				}}/>
			</Row>

			{/* display all summary messages first, so they're easy to locate*/}
			{/*messages_summaries_onesToShow.map((message, index)=>{
				return <StoryMessageUI key={index} story={entry} message={message} messages={messages}/>;
			})}
			{messages_normal_onesToShow.map((message, index)=>{
				return <StoryMessageUI key={index} story={entry} message={message} messages={messages}/>;
			})*/}

			{messagesToShow_chronological.map((message, index)=>{
				return <StoryMessageUI key={index} story={entry} message={message} messages={messages} messagesShown={messagesToShow_chronological} mainChain={mainChain} verboseChain={verboseChain}/>;
			})}

			{/*<Row mt={5}>
				<Button text="Add message" onClick={()=>{
					const segments_new = Clone(entry.segments);
					segments_new.push(new JournalSegment());
					new UpdateJournalEntry({id: entry.id, updates: {segments: segments_new}}).RunOnServer();
				}}/>
			</Row>*/}
		</ScrollView>
	);
});